<template>
  <Teleport to="body">
    <div class="toast-area">
      <ToastComponent :visible="toastVisible" :type="toastType" :message="toastMessage" />
    </div>
  </Teleport>

  <div v-if="visible" class="modal-overlay">
    <div class="modal-container">
      <div class="modal-header">
        <h2 class="modal-title">Apply to Another Template</h2>
        <button class="close-btn" type="button" :disabled="submitting" @click="handleClose">
          <img :src="closeIcon" alt="close" class="close-icon" />
        </button>
      </div>

      <div class="modal-body">
        <div class="form-group">
          <label class="form-label">
            <span class="required">*</span> Target Season Template
          </label>
          <div ref="templateSelectRef" class="custom-select">
            <div
              class="select-trigger"
              :class="{
                'has-placeholder': !selectedSeasonKey,
                error: showValidationError && !selectedSeasonKey,
                open: templateDropdownOpen,
              }"
              @click="templateDropdownOpen = !templateDropdownOpen"
            >
              <span class="select-value">{{ templateDisplayValue }}</span>
              <img :src="downArrowIcon" alt="arrow" class="select-arrow" />
              <img
                v-if="showValidationError && !selectedSeasonKey"
                :src="alertIcon"
                alt="error"
                class="error-icon"
              />
            </div>
          </div>
        </div>
      </div>

      <div class="modal-footer">
        <button class="btn-cancel" type="button" :disabled="submitting" @click="handleClose">
          Cancel
        </button>
        <button class="btn-submit" type="button" :disabled="submitting" @click="handleApply">
          Apply
        </button>
      </div>
    </div>
  </div>

  <Teleport to="body">
    <div
      v-if="visible && templateDropdownOpen"
      class="dropdown-backdrop"
      @click.self="templateDropdownOpen = false"
    >
      <div class="select-dropdown" :style="templateDropdownStyle">
        <div v-if="loadingOptions" class="select-empty select-empty--loading">
          <span class="dropdown-spinner" aria-hidden="true"></span>
        </div>
        <template v-else-if="seasonOptions.length > 0">
          <div
            v-for="option in seasonOptions"
            :key="option.value"
            class="select-option"
            :class="{ selected: option.value === selectedSeasonKey }"
            @click="selectSeason(option.value)"
          >
            {{ option.label }}
          </div>
        </template>
        <div v-else class="select-empty">No available templates</div>
      </div>
    </div>
  </Teleport>

  <!-- 二次确认：530×212，盖在应用弹窗之上 -->
  <div v-if="showConfirmModal" class="modal-overlay confirm-overlay">
    <div class="confirm-container">
      <div class="confirm-header">
        <h2 class="confirm-title">Confirm Replacement</h2>
        <button class="close-btn" type="button" :disabled="submitting" @click="closeConfirm">
          <img :src="closeIcon" alt="close" class="close-icon" />
        </button>
      </div>

      <div class="confirm-body">
        <p>
          This will apply the current items to the selected template. Existing items in the target
          template will be replaced.
        </p>
        <p>Are you sure you want to continue?</p>
      </div>

      <div class="confirm-footer">
        <button class="btn-no" type="button" :disabled="submitting" @click="closeConfirm">
          No
        </button>
        <button class="btn-submit" type="button" :disabled="submitting" @click="confirmApply">
          Confirm
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import ToastComponent from '@/components/ToastComponent.vue'
import closeIcon from '@/assets/Fill 1@2x.png'
import downArrowIcon from '@/assets/downarrow@2x.png'
import alertIcon from '@/assets/alert.png'

import { getDefaultChecklistList, applyDefaultChecklist } from '@/api/defaultChecklist'
import type {
  DefaultChecklistApplyData,
  DefaultChecklistSeasonKey,
} from '@/api/defaultChecklist'
import { formatSeasonDisplayLabel } from '@/utils/seasons'

export type SeasonKey = DefaultChecklistSeasonKey

export interface SeasonOption {
  label: string
  value: SeasonKey
  hasItems: boolean
  templateId?: number | null
}

interface Props {
  visible: boolean
  sourceSeasonKey: SeasonKey | null
  sourceTemplateId?: number | null
  seasonOptions: SeasonOption[]
}

const props = defineProps<Props>()
const emit = defineEmits<{
  (e: 'close'): void
  (e: 'applied', payload: { targetSeasonKey: SeasonKey; data?: DefaultChecklistApplyData }): void
}>()

const selectedSeasonKey = ref<SeasonKey | null>(null)
const submitting = ref(false)
const showConfirmModal = ref(false)
const showValidationError = ref(false)
const templateDropdownOpen = ref(false)
const templateSelectRef = ref<HTMLElement | null>(null)

const toastVisible = ref(false)
const toastType = ref<'success' | 'warning' | 'error'>('success')
const toastMessage = ref('')
let toastTimer: ReturnType<typeof setTimeout> | null = null

const loadedOptions = ref<SeasonOption[]>([])
const loadingOptions = ref(false)

const seasonOptions = computed(() => {
  const source = loadedOptions.value.length > 0 ? loadedOptions.value : props.seasonOptions
  return source.filter((opt) => opt.value !== props.sourceSeasonKey)
})

const templateDisplayValue = computed(() => {
  if (!selectedSeasonKey.value) {
    return 'Select template'
  }
  const option = seasonOptions.value.find((opt) => opt.value === selectedSeasonKey.value)
  return option ? option.label : 'Select template'
})

const templateDropdownStyle = computed(() => {
  if (!templateSelectRef.value || !templateDropdownOpen.value) return {}
  const rect = templateSelectRef.value.getBoundingClientRect()
  return {
    position: 'fixed' as const,
    top: `${rect.bottom + 4}px`,
    left: `${rect.left}px`,
    width: `${rect.width}px`,
  }
})

function showToast(type: 'success' | 'warning' | 'error', message: string): void {
  if (toastTimer) {
    clearTimeout(toastTimer)
  }
  toastType.value = type
  toastMessage.value = message
  toastVisible.value = true
  toastTimer = setTimeout(() => {
    toastVisible.value = false
  }, 3500)
}

async function loadTargetOptions(): Promise<void> {
  loadingOptions.value = true
  try {
    const params =
      props.sourceTemplateId != null ? { exclude_template_id: props.sourceTemplateId } : {}
    const response = await getDefaultChecklistList(params)
    if (response.code !== 200 || !response.data) {
      loadedOptions.value = []
      return
    }

    const list = response.data.list ?? []
    const options = response.data.options ?? []
    const idBySeason = new Map(list.map((tpl) => [tpl.season_key, tpl.id]))
    const itemCountBySeason = new Map(
      list.map((tpl) => [tpl.season_key, tpl.item_count || tpl.items?.length || 0]),
    )

    if (options.length > 0) {
      loadedOptions.value = options.map((opt) => ({
        label: formatSeasonDisplayLabel(opt.label) || opt.label,
        value: opt.season_key,
        hasItems: (itemCountBySeason.get(opt.season_key) ?? 0) > 0,
        templateId: idBySeason.get(opt.season_key) ?? opt.value ?? opt.id,
      }))
      return
    }

    loadedOptions.value = list.map((tpl) => {
      const label = tpl.season_name || tpl.template_name
      return {
        label: formatSeasonDisplayLabel(label) || label,
        value: tpl.season_key,
        hasItems: (tpl.item_count || tpl.items?.length || 0) > 0,
        templateId: tpl.id,
      }
    })
  } catch {
    loadedOptions.value = []
  } finally {
    loadingOptions.value = false
  }
}

function selectSeason(value: SeasonKey): void {
  selectedSeasonKey.value = value
  templateDropdownOpen.value = false
}

function handleClickOutside(event: MouseEvent): void {
  if (templateSelectRef.value && !templateSelectRef.value.contains(event.target as Node)) {
    templateDropdownOpen.value = false
  }
}

function handleApply(): void {
  if (submitting.value) return
  if (!selectedSeasonKey.value) {
    showValidationError.value = true
    showToast('warning', 'Please complete all required fields.')
    return
  }

  // 每次应用都弹出二次确认
  showConfirmModal.value = true
}

async function confirmApply(): Promise<void> {
  if (!selectedSeasonKey.value || submitting.value) return

  const target = seasonOptions.value.find((opt) => opt.value === selectedSeasonKey.value)
  const targetTemplateId = target?.templateId ?? null
  if (props.sourceTemplateId == null || targetTemplateId == null) {
    showToast('error', 'Missing template id')
    return
  }
  if (props.sourceTemplateId === targetTemplateId) {
    showToast('error', 'Please select a different season template')
    return
  }

  submitting.value = true
  try {
    const payload = {
      source_id: props.sourceTemplateId,
      target_id: targetTemplateId,
      source_template_id: props.sourceTemplateId,
      target_template_id: targetTemplateId,
      confirm_replace: 1,
    }

    let response = await applyDefaultChecklist(payload)
    if (response.code === 409 && response.data?.need_confirm_replace) {
      response = await applyDefaultChecklist(payload)
    }

    if (response.code === 200) {
      emit('applied', {
        targetSeasonKey: selectedSeasonKey.value,
        data: response.data,
      })
      showConfirmModal.value = false
      emit('close')
      showToast('success', 'Successfully applied')
    } else {
      showToast('error', response.msg || 'Failed to apply')
    }
  } catch {
    showToast('error', 'Failed to apply')
  } finally {
    submitting.value = false
  }
}

function closeConfirm(): void {
  if (submitting.value) return
  showConfirmModal.value = false
}

function handleClose(): void {
  if (submitting.value) return
  selectedSeasonKey.value = null
  showValidationError.value = false
  templateDropdownOpen.value = false
  showConfirmModal.value = false
  emit('close')
}

watch(
  () => props.visible,
  (newVal) => {
    if (newVal) {
      loadedOptions.value = []
      void loadTargetOptions()
      return
    }
    selectedSeasonKey.value = null
    showValidationError.value = false
    templateDropdownOpen.value = false
    showConfirmModal.value = false
    loadedOptions.value = []
  },
)

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
})
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
}

.confirm-overlay {
  z-index: 10001;
  background-color: rgba(0, 0, 0, 0.35);
}

.modal-container {
  background-color: #ffffff;
  border-radius: 16px;
  width: 530px;
  height: 212px;
  display: flex;
  flex-direction: column;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
  box-sizing: border-box;
}

.confirm-container {
  background-color: #ffffff;
  border-radius: 16px;
  width: 530px;
  height: 212px;
  display: flex;
  flex-direction: column;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
  box-sizing: border-box;
}

.modal-header,
.confirm-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px 24px 0;
  flex-shrink: 0;
}

.modal-title,
.confirm-title {
  font-size: 16px;
  font-weight: 700;
  font-family:
    SFPro,
    'SF Pro',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  color: #000000;
  margin: 0;
}

.close-btn {
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.close-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.close-icon {
  width: 16px;
  height: 16px;
  object-fit: contain;
}

.modal-body {
  padding: 16px 24px 0;
  display: flex;
  flex-direction: column;
  gap: 16px;
  flex: 1;
  min-height: 0;
}

.confirm-body {
  flex: 1;
  padding: 16px 24px 0;
  font-size: 14px;
  color: #000000;
  line-height: 1.6;
  font-family:
    SFPro,
    'SF Pro',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  font-weight: 400;
  text-align: left;
  box-sizing: border-box;
  min-height: 0;
}

.confirm-body p {
  margin: 0;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.form-label {
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  font-family:
    SFPro,
    'SF Pro',
    'SF Pro Text',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  color: #000000;
}

.required {
  color: #ff4d4f;
  margin-right: 4px;
}

.custom-select {
  position: relative;
}

.select-trigger {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 40px 0 16px;
  border: 1px solid #e0e0e0;
  border-radius: 50px;
  background-color: #ffffff;
  cursor: pointer;
  transition: all 0.2s;
  width: 100%;
  height: 32px;
  box-sizing: border-box;
  position: relative;
}

.select-trigger.has-placeholder {
  color: #cccccc;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
}

.select-trigger:not(.has-placeholder) {
  color: #000000;
}

.select-trigger:not(.has-placeholder) .select-value {
  color: #000000;
}

.select-trigger.open,
.select-trigger:focus-within:not(.error) {
  border-color: #666666;
}

.select-trigger.error {
  border-color: #ff4d4f;
  background: #fffafa;
}

.select-value {
  flex: 1;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  font-family:
    SFPro,
    'SF Pro',
    'SF Pro Text',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.select-arrow {
  width: 16px;
  height: 16px;
  max-width: 16px;
  max-height: 16px;
  object-fit: contain;
  opacity: 0.55;
  transition: transform 0.2s;
  flex-shrink: 0;
  position: absolute;
  right: 16px;
}

.error-icon {
  position: absolute;
  right: 40px;
  width: 16px;
  height: 16px;
  object-fit: contain;
  pointer-events: none;
}

.select-trigger.open .select-arrow {
  transform: rotate(180deg);
}

.dropdown-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 99999;
}

.select-dropdown {
  background-color: #ffffff;
  border: 1px solid #e0e0e0;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  max-height: 200px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 4px 0;
  box-sizing: border-box;
}

.select-option {
  display: flex;
  align-items: center;
  padding: 8px 16px;
  line-height: 20px;
  cursor: pointer;
  transition: background-color 0.2s;
  color: #000000;
  font-size: 14px;
  font-weight: 400;
  font-family:
    SFPro,
    'SF Pro',
    'SF Pro Text',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
}

.select-option:hover {
  background-color: #f5f5f5;
}

.select-option.selected {
  background-color: transparent;
  color: #000000;
}

.select-empty {
  padding: 12px 16px;
  color: #999;
  font-size: 14px;
  text-align: center;
  border-radius: 12px;
}

.select-empty--loading {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 40px;
}

.dropdown-spinner {
  width: 20px;
  height: 20px;
  border: 2px solid #e0e0e0;
  border-top-color: #00aeef;
  border-radius: 50%;
  animation: dropdown-spin 0.8s linear infinite;
  flex-shrink: 0;
}

@keyframes dropdown-spin {
  to {
    transform: rotate(360deg);
  }
}

.modal-footer,
.confirm-footer {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 16px;
  padding: 0 24px 24px;
  flex-shrink: 0;
}

.btn-cancel,
.btn-no {
  width: 80px;
  height: 32px;
  padding: 0;
  border: 1px solid #000000;
  border-radius: 16px;
  background-color: #ffffff;
  color: #000000;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  font-family:
    SFPro,
    'SF Pro',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-cancel:hover:not(:disabled),
.btn-no:hover:not(:disabled) {
  background-color: #000000;
  color: #ffffff;
}

.btn-cancel:disabled,
.btn-no:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-submit {
  width: 80px;
  height: 32px;
  padding: 0;
  border: none;
  border-radius: 16px;
  background-color: #000000;
  color: #ffffff;
  font-weight: 400;
  line-height: 20px;
  font-family:
    SFPro,
    'SF Pro',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-submit:hover:not(:disabled) {
  background-color: #484848;
}

/* loading：灰底 + 白圈（配合全局 .btn-submit:disabled::after） */
.btn-submit:disabled {
  opacity: 1;
  background-color: #666666;
  cursor: not-allowed;
}
</style>
